import {
    faAngleDown,
    faAngleUp,
    faCheck,
    faLock,
    faTrashAlt,
    faTriangleExclamation
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { FC, useMemo, useState } from "react";
import {
    Button,
    Card,
    Col,
    Collapse,
    FormControl,
    InputGroup,
    Row
} from "react-bootstrap";
import {
    AnalyticsEventDataSchema,
    AnalyticsEventSchema,
    AnalyticsSchema,
    User
} from "../../../models/types";
import { AnalyticsEventDataLibraryItem } from "./AnalyticsEventDataLibraryItem";

interface Props {
    event: AnalyticsEventSchema;
    updateAnalyticsSchema: (event: AnalyticsEventSchema) => void;
    removeLibEvent?: (eventId: number) => void;
    user?: User;
    currentSchema: AnalyticsSchema | undefined;
}

export const AnalyticsEventLibraryItemImplementation: FC<Props> = ({
    event,
    updateAnalyticsSchema,
    removeLibEvent,
    currentSchema,
    user
}) => {
    const [showEventData, updateShowEventData] = useState(false);
    const [showCheck, setShowCheck] = useState(false);

    const updateSchema = () => {
        updateAnalyticsSchema(event);
        setShowCheck(true);
        setTimeout(() => setShowCheck(false), 300);
    };

    const includedInCurrentSchema = useMemo(() => {
        if (!currentSchema || !currentSchema.events) {
            return false;
        }
        return currentSchema.events.some(
            schemaEvent => schemaEvent.eventName === event.eventName
        );
    }, [currentSchema, event.eventName]);

    const eventData = event.eventData.map(
        (eventData: AnalyticsEventDataSchema, index: number) => (
            <AnalyticsEventDataLibraryItem
                frozen={true}
                index={index}
                key={index}
                defaultValue={eventData}
                updateAnalyticsDataSchema={() =>
                    console.log("[DEBUG] Nothing should happen")
                }
            />
        )
    );

    return (
        <Card className="mb-2 mx-2">
            <Card.Header
                onClick={() => {
                    updateShowEventData(!showEventData);
                }}
                className="no-border"
            >
                <Row className="d-flex align-items-center">
                    <Col className="col-8 d-flex align-items-center">
                        {!event.frozen &&
                        user?.scopes?.includes("admin") &&
                        user?.organization?.name === "Seepia" &&
                        removeLibEvent ? (
                            <FontAwesomeIcon
                                icon={faTrashAlt}
                                title="Remove event"
                                className="text-primary mouseHover pe-3"
                                onClick={e => {
                                    e.stopPropagation();
                                    removeLibEvent(event.id);
                                }}
                            />
                        ) : null}
                        <InputGroup>
                            <InputGroup.Text>
                                <span className="d-none d-lg-block">Event</span>
                                <span>&nbsp;Name</span>
                            </InputGroup.Text>
                            <FormControl
                                value={event.eventName}
                                readOnly={true}
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                }}
                            />
                        </InputGroup>
                    </Col>
                    <Col className="col-1">
                        <FontAwesomeIcon
                            icon={showEventData ? faAngleUp : faAngleDown}
                            size="lg"
                            className="mouseHover text-secondary"
                        />
                    </Col>
                    <Col className="d-flex align-items-center justify-content-end col-3">
                        {!event.frozen ? (
                            <>
                                {showCheck && (
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className="text-success pe-3"
                                    />
                                )}
                                <Button
                                    onClick={(
                                        e: React.MouseEvent<HTMLElement>
                                    ) => {
                                        e.stopPropagation();
                                        updateSchema();
                                    }}
                                    className="mt-0 btn-success"
                                >
                                    Add
                                </Button>
                            </>
                        ) : (
                            <>
                                {includedInCurrentSchema ? (
                                    <div className="pe-3">
                                        <FontAwesomeIcon
                                            icon={faLock}
                                            className="text-secondary"
                                            size="lg"
                                            title="Event Frozen by Admin"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <FontAwesomeIcon
                                            icon={faTriangleExclamation}
                                            className="text-warning pe-3"
                                            title="Add Missing Mandatory Event!"
                                        />
                                        <Button
                                            onClick={(
                                                e: React.MouseEvent<HTMLElement>
                                            ) => {
                                                e.stopPropagation();
                                                updateSchema();
                                            }}
                                            className="mt-0 btn-success"
                                        >
                                            Add
                                        </Button>
                                    </>
                                )}
                            </>
                        )}
                    </Col>
                </Row>
            </Card.Header>
            <Collapse in={showEventData}>
                <div>
                    <Card.Body className="p-4">
                        {eventData}
                        <InputGroup>
                            <InputGroup.Text>Comment</InputGroup.Text>
                            <FormControl
                                as="textarea"
                                rows={3}
                                value={event.comment ? event.comment : ""}
                                readOnly={true}
                            />
                        </InputGroup>
                    </Card.Body>
                </div>
            </Collapse>
        </Card>
    );
};

export const AnalyticsEventLibraryItem = React.memo(
    AnalyticsEventLibraryItemImplementation
);
